import { useParams } from 'react-router-dom';
import PageScaffold from '../../components/PageScaffold';
import { SUBSCRIPTIONS_PAGE_PATH } from '../subscriptions/SubscriptionsPage';
import { CONTACTS_PAGE_PATH } from './ContactsPage';
import { useContext, useEffect } from 'react';
import { Alert, Button, Card } from 'antd';
import ContactsContext from '../../contexts/data/ContactsContext';
import { ContextStatus } from '@leedpulse-monorepo/enums';
import { ContactCreationFormProvider } from '../../contexts/forms/contacts/ContactCreationFormContext';
import { ContactsImportFormProvider } from '../../contexts/forms/contacts/ContactsImportFormContext';
import { ContactEditionFormProvider } from '../../contexts/forms/contacts/ContactEditionFormContext';
import ContactConversationLink from '../../components/contact_detail_page/ContactConversationLink';
import ContactCreationForm from '../../components/contact_detail_page/ContactCreationForm';
import ContactsImportForm from '../../components/contact_detail_page/ContactsImportForm';
import ContactDetails from '../../components/contact_detail_page/ContactDetails';
import { ClockCircleOutlined, ReloadOutlined } from '@ant-design/icons';
import JobsList from '../../components/@shared/JobsList';
import JobsContext from '../../contexts/data/JobsContext';
import ContactLastActivityRole from '../../../domain/enums/contact/contact_last_activity/ContactLastActivityRole';
import Scenario from '../../../domain/models/Scenario';
import ScenarioBusinessPriority from '../../../domain/enums/scenario/ScenarioBusinessPriority';

const globalScenarios = [
  new Scenario('unknown', 'unknown', 'unknown', 'unknown', ScenarioBusinessPriority.one, 'unknown', 'unknown', [], 'unknown', 'unknown', [], false, false, 'unknown', [], new Date(), new Date(), ''),
];

interface ContactDetailPageProps {
  isCreateMode: boolean;
  isImportMode: boolean;
  withSubscriptionIdScope?: boolean;
}

const ContactDetailPage: React.FC<ContactDetailPageProps> = ({
  isCreateMode,
  isImportMode,
  withSubscriptionIdScope,
}) => {
  const contactsContext = useContext(ContactsContext);
  const jobsContext = useContext(JobsContext);

  const { subscriptionId: subscriptionIdParam, contactId = '' } = useParams<{
    subscriptionId: string;
    contactId: string;
  }>();
  const subscriptionId =
    withSubscriptionIdScope && subscriptionIdParam ? subscriptionIdParam : null;

  const selectionMismatch =
    (!isCreateMode && contactsContext.selected === null) ||
    (!isCreateMode && contactsContext.selected?.id !== contactId);

  useEffect(() => {
    if (
      selectionMismatch &&
      contactsContext.selectionStatus !== ContextStatus.LOADING
    ) {
      contactsContext.fetchAndSelectById(contactId);
    }

    if (
      jobsContext.status !== ContextStatus.LOADING ||
      jobsContext.scopedSubscriptionId !== subscriptionId
    ) {
      jobsContext.fetch(null, contactId);
    }
    return () => {
      contactsContext.reset();
      jobsContext.reset();
    };
  }, []);

  return (
    <PageScaffold
      title={
        isCreateMode
          ? 'Nouveau contact'
          : isImportMode
            ? 'Importation de contact(s)'
            : 'Détail contact'
      }
      subtitle={
        isCreateMode
          ? "Ajoutez un nouveau contact à l'abonnement"
          : isImportMode
            ? 'Importez des contacts via fichier plat'
            : 'Consultez et modifiez les informations du contact'
      }
      breadcrumbItems={
        withSubscriptionIdScope
          ? [
            {
              target:
                SUBSCRIPTIONS_PAGE_PATH +
                '/' +
                subscriptionId +
                CONTACTS_PAGE_PATH,
              title: 'Contacts',
            },
            {
              title: isCreateMode
                ? 'Nouveau'
                : isImportMode
                  ? 'Importation'
                  : (contactsContext.selected?.contact.firstname || '') + ' ' + (contactsContext.selected?.contact.lastname || ''),
            },
          ]
          : [
            {
              target: CONTACTS_PAGE_PATH,
              title: 'Contacts',
            },
            {
              title: (contactsContext.selected?.contact.firstname || '') + ' ' + (contactsContext.selected?.contact.lastname || ''),
            },
          ]
      }
    >
      {isCreateMode && subscriptionId ? (
        <ContactCreationFormProvider>
          <ContactCreationForm subscriptionId={subscriptionId} globalScenarios={globalScenarios} />
        </ContactCreationFormProvider>
      ) : isImportMode && subscriptionId ? (
        <ContactsImportFormProvider>
          <ContactsImportForm subscriptionId={subscriptionId} globalScenarios={globalScenarios} />
        </ContactsImportFormProvider>
      ) : (
        <>
          <ContactConversationLink isConversationEmpty={contactsContext.selected?.last_activity.role === '' as ContactLastActivityRole} />
          {contactsContext.selectionStatus === ContextStatus.ERROR ? (
            // Sans ce cas, un echec de chargement rendait un `Card loading`
            // que rien n'arretait : la page paraissait « ne pas se charger »
            // alors qu'elle avait deja renonce.
            <Alert
              type="error"
              showIcon
              message="Impossible de charger ce contact."
              description={contactsContext.errorMessage || 'Erreur inconnue.'}
            />
          ) : selectionMismatch ||
            contactsContext.selected == null ||
            (contactsContext.selectionSubscription == null && contactsContext.selectionCompany == null) ||
            contactsContext.selectionOwners == null ? (
            <Card loading />
          ) : (
            <ContactEditionFormProvider>
              <ContactDetails
                contact={contactsContext.selected}
                subscription={contactsContext.selectionSubscription}
                company={contactsContext.selectionCompany}
                subscriptionId={subscriptionId}
                globalScenarios={globalScenarios}
                owners={contactsContext.selectionOwners}
              />
            </ContactEditionFormProvider>
          )}
          {
            jobsContext.count > 0 &&
            <div className="list-container">
              <div className="list-container__controls">
                <h2><ClockCircleOutlined style={{ marginRight: 6 }} /> Messages planifiés</h2>
                <Button
                  type="primary"
                  onClick={() => jobsContext.fetch(null, contactId)}
                >
                  <ReloadOutlined />
                  Rafraîchir
                </Button>
              </div>

              <JobsList scopedSubscriptionId={null} scopedContactId={contactId} />
            </div>
          }
        </>
      )}
    </PageScaffold>
  );
};

export default ContactDetailPage;
